import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs';
import { BaseUrlService } from '../base-url.service';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class TokenInterceptorService<T> implements HttpInterceptor {
  public api: string;
  public identity: string;
  private ignoreRoutes: Array<string> = [];
  public refreshTokenURL: string;
  private _isRefreshingToken = false;

  constructor(
    private authenticationService: AuthenticationService,
    private baseUrlService: BaseUrlService
  ) {
    this.api = this.baseUrlService.getApiBaseURL();
    this.identity = this.baseUrlService.getIdentityBaseURL();
    this.refreshTokenURL = `${this.identity}api/master/refreshToken`;
    this.ignoreRoutes.push(`${this.identity}api/master/acquireToken`);
    this.ignoreRoutes.push(`${this.identity}api/master/acquireTokenTfaCheck`);
    this.ignoreRoutes.push(`${this.identity}api/master/validateUserOtp`);
    this.ignoreRoutes.push(`${this.identity}api/master/resendOtp`);
    this.ignoreRoutes.push(`${this.identity}api/account/reset-password`);
    this.ignoreRoutes.push(`${this.identity}api/account/reset-password-check`);
    this.ignoreRoutes.push(`${this.identity}api/account/reset-password`);
    this.ignoreRoutes.push(`${this.api}api/account/user/saveUserName`);
    this.ignoreRoutes.push(`${this.api}api/tpi/customer/visit/save`);
    this.ignoreRoutes.push(`${this.identity}api/account/user/access-logout`);
    this.ignoreRoutes.push(`${this.api}api/notifications/fcmToken/delete`);
    this.ignoreRoutes.push(`${this.api}api/tpi/external-sales-agents/get`);
    this.ignoreRoutes.push(`${this.identity}api/organization/org-logo/get`);
    this.ignoreRoutes.push(`${this.api}api/account/properties/get`);
    this.ignoreRoutes.push(`${this.api}api/common/configurations`);
    this.ignoreRoutes.push(`${this.api}api/tpi/website/lead`);
    this.ignoreRoutes.push(`${this.api}api/tpi/property-logo/get`);
    this.ignoreRoutes.push(`${this.api}api/tpi/property-quotation/save`);
    this.ignoreRoutes.push(`${this.api}api/tpi/external-cp-form/save`);
    this.ignoreRoutes.push(`${this.api}api/tpi/external-form/send-otp`);
    this.ignoreRoutes.push(`${this.api}api/tpi/external-form/validate-otp`);
    this.ignoreRoutes.push(`${this.api}api/account/aliasName`);
    this.ignoreRoutes.push(`${this.api}api/account/user-reset-password-otp-send`);
    this.ignoreRoutes.push(`${this.api}api/account/user-reset-password-otp-verify`);
    this.ignoreRoutes.push(`${this.api}api/account/reset-password`);
    this.ignoreRoutes.push(`${this.api}api/account/reset-password-new`);
  }

  public intercept(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.startsWithAPI(req.url) || this.isIgnoredRoute(req)) {
      return next.handle(req);
    }
    if (this.isSameRoute(req.url, this.refreshTokenURL)) {
      return this.handleError(next.handle(req));
    }
    return this.handleError(this.handleRefreshToken(req, next));
  }

  private handleRefreshToken(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.authenticationService.isUserExpired()) {
      if (!this._isRefreshingToken) {
        this._isRefreshingToken = true;
        this.authenticationService.$token.next(null);
        return this.authenticationService.refreshToken().pipe(
          switchMap(_ => {
            this._isRefreshingToken = false;
            return this.$addTokenToHeader(req, next);
          })
        );
      }
      return this.authenticationService.$token.pipe(
        filter(_ => _ !== null),
        take(1),
        switchMap(_ => next.handle(this.addTokenToHeader(req, _)))
      );
    }
    return this.$addTokenToHeader(req, next);
  }

  private startsWithAPI(url: string): boolean {
    return url.startsWith(this.api) || url.startsWith(this.identity);
  }

  private isIgnoredRoute(req: HttpRequest<T>): boolean {
    for (const route of this.ignoreRoutes) {
      if (this.isSameRoute(req.url, route)) {
        return true;
      }
    }

    return false;
  }

  private isSameRoute(req: string, url: string): boolean {
    return req.split('?')[0] === url;
  }

  private handleError(
    res: Observable<HttpEvent<unknown>>
  ): Observable<HttpEvent<unknown>> {
    return res.pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 401:
              this.authenticationService.logOut();
              break;
            default:
              return throwError(err.error);
          }
        }
        return of(err);
      })
    );
  }

  protected $addTokenToHeader(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.authenticationService.$token.pipe(
      filter(_ => _ !== null),
      take(1),
      switchMap(token => {
        const request = this.addTokenToHeader(req, token);
        return next.handle(request);
      })
    );
  }

  protected addTokenToHeader(
    req: HttpRequest<T>,
    token: {
      token: string | null;
      apiKey: string | null;
    } | null
  ): HttpRequest<T> {
    return req.clone({
      setHeaders: {
        AUTHORIZATION: `Bearer ${token?.token}`,
        'X-API-KEY': `${token?.apiKey}`
      }
    });
  }
}
